import { splitGroupsOf } from "@CommonUtils/pog/aisleSplits";
import { PlanogramPegboard, PlanogramPegboardNonCircular } from "./pegboard";
import { Planogram } from "./planogram";
import { PlanogramShelf, PlanogramShelfNonCircular } from "./shelf";
import { liveStartOf } from "@CommonUtils/pog/shelfMerchandising";

export type PlanogramPalletAccessSide = "front" | "back" | "left" | "right";

/**
 * A floor pallet. The bay that carries it is the pallet, and the bay's shelves
 * are its depth lanes (`laneNo` 1 = the front lane), all standing on the deck.
 */
export interface PlanogramBayPallet {
  /** 1 = leftmost as the shopper faces the pallets. */
  palletNo: number;
  placement: "standalone" | "run" | "under_bay";
  /**
   * The uniqueId of the gondola bay an `under_bay` pallet stands under; null
   * otherwise. An id, not a position, so moving bays never re-homes a pallet.
   */
  bayUniqueId: string | null;
  laneDepth: number;
  lanes: number;
  /** Stacking height above the deck. */
  maxHeight: number;
  /** The sides a shopper can reach. A PSA has no field for it. */
  accessSides: PlanogramPalletAccessSide[];
  maxSkus: number;
}

interface PlanogramBayCommon {
  uniqueId: string;
  width: number;
  height: number;
  depth: number;
  baseHeight: number;
  notchOffset: number;
  notchSpacing: number;
  maxNotch: number;
  maxShelfHeight: number;
  maxProductHead: number;
  /** The run physically breaks after this bay. Declared on the store's fixtures, never
   *  inferred; ignored on the last bay. */
  splitAfter?: boolean;
  /** Set on a floor pallet only; a gondola bay does not carry the key. */
  pallet?: PlanogramBayPallet;
}

export interface PlanogramBayNonCircular extends PlanogramBayCommon {
  shelves: PlanogramShelfNonCircular[];
  pegboards: PlanogramPegboardNonCircular[];
}

export class PlanogramBay implements PlanogramBayCommon {
  uniqueId: string;
  width: number;
  height: number;
  depth: number;
  baseHeight: number;
  notchOffset: number;
  notchSpacing: number;
  maxNotch: number;
  maxShelfHeight: number;
  maxProductHead: number;
  /** See PlanogramBayCommon — normalized to a boolean, false for older pogs. */
  splitAfter: boolean;
  pallet?: PlanogramBayPallet;

  shelves: PlanogramShelf[];
  pegboards: PlanogramPegboard[];
  planogram: Planogram;

  constructor(bay: PlanogramBayNonCircular, planogram: Planogram) {
    this.uniqueId = bay.uniqueId;
    this.width = bay.width;
    this.height = bay.height;
    this.depth = bay.depth;
    this.baseHeight = bay.baseHeight;
    this.notchOffset = bay.notchOffset;
    this.notchSpacing = bay.notchSpacing;
    this.maxNotch = bay.maxNotch;
    this.maxProductHead = bay.maxProductHead;
    this.maxShelfHeight = bay.maxShelfHeight;
    this.splitAfter = bay.splitAfter ?? false;
    if (bay.pallet) {
      this.pallet = { ...bay.pallet, accessSides: [...bay.pallet.accessSides] };
    }
    this.shelves = bay.shelves.map((shelf) => new PlanogramShelf(shelf, this));
    this.pegboards = bay.pegboards.map((pegboard) => new PlanogramPegboard(pegboard, this));
    this.planogram = planogram;
  }

  getNo = () => {
    return this.planogram.bays.findIndex((bay) => bay.uniqueId === this.uniqueId) + 1;
  };

  /**
   * The gondola bay this pallet stands under, when it stands under one. Such a
   * pallet takes no floor of its own along the run: it is drawn where its bay is.
   */
  getBayAbove = (): PlanogramBay | null => {
    const bayUniqueId = this.pallet?.placement === "under_bay" ? this.pallet.bayUniqueId : null;
    if (bayUniqueId === null) {
      return null;
    }
    const bayAbove = this.planogram.bays.find((bay) => bay.uniqueId === bayUniqueId);
    return bayAbove && !bayAbove.pallet ? bayAbove : null;
  };

  getXLeft = (): number => {
    const bayAbove = this.getBayAbove();
    if (bayAbove) {
      return bayAbove.getXLeft();
    }

    let xLeft = 0;

    for (const bay of this.planogram.bays) {
      if (bay.uniqueId === this.uniqueId) {
        break;
      }

      if (!bay.getBayAbove()) {
        xLeft += bay.width;
      }
    }

    return xLeft;
  };

  getXRight = () => {
    return this.getXLeft() + this.width;
  };

  /** The pogger's `split_group`: shelves in different groups never merge. */
  getSplitGroup = () => splitGroupsOf(this.planogram.bays)[this.getNo() - 1] ?? 1;

  fixShelvesOrder = () => {
    // Top first. Two boards at one notch (a rack's part of a shelf beside the
    // rest of it) keep a fixed order: the one whose live part starts further left first.
    this.shelves = this.shelves.sort(
      (shelf1, shelf2) =>
        shelf2.getNotchNo() - shelf1.getNotchNo() ||
        liveStartOf(shelf1.deadSpans) - liveStartOf(shelf2.deadSpans),
    );
  };

  addShelf = (shelf: PlanogramShelf) => {
    this.shelves.push(shelf);
    this.fixShelvesOrder();
    return shelf;
  };

  addPegboard(pegboard: PlanogramPegboard) {
    this.pegboards.push(pegboard);
    return pegboard;
  }

  delete = () => {
    for (const shelf of this.shelves) {
      shelf.delete();
    }

    this.planogram.bays = this.planogram.bays.filter((bay) => bay.uniqueId !== this.uniqueId);
  };
}
